<template>
  <div class="tw:flex tw:flex-nowrap tw:w-full tw:gap-4">
    <div class="tw:w-6 tw:h-6 tw:flex tw:items-center tw:justify-center">
      <img
        v-if="type === 'success'"
        alt="Success icon"
        src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/circle-check.svg"
      />
      <img
        v-if="type === 'error'"
        alt="Error icon"
        src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/octagon-exclamation.svg"
      />
      <img
        v-if="type === 'warning'"
        alt="Waring icon"
        src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/triangle-exclamation.svg"
      />
      <img
        v-if="type === 'default'"
        alt="Default icon"
        src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/circle-info.svg"
      />
      <img
        v-if="type === 'info'"
        alt="Inbox icon"
        src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/message-lines.svg"
      />
    </div>
    <div class="tw:flex tw:flex-col tw:w-[20rem]">
      <div v-if="typeof message === 'string'" class="tw:body-1--bold">
        {{ message }}
      </div>

      <div v-else class="tw:flex tw:flex-col tw:gap-2">
        <div class="tw:flex tw:flex-col tw:gap-1">
          <div class="tw:body-1--bold tw:text-black">
            {{ message.message }}
          </div>
          <div v-if="message.desc" class="tw:body-1 tw:!text-default-700">
            {{ message.desc }}
          </div>
        </div>

        <div v-if="message.link" class="tw:flex tw:items-start tw:gap-3">
          <EdroneButton :type="message.link.buttonType || 'link-outline'" @click="handleButtonClick(message.link)">
            {{ message.link.text }}
            <template v-if="message.link.newTab" #right-icon>
              <img
                alt="Link open icon"
                src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/arrow-up-right-from-square-newtab.svg"
              />
            </template>
          </EdroneButton>
          <qrcode
            v-if="message?.link?.url?.includes('edrone_version')"
            class="tw:-mt-2.5"
            :options="{ width: 175 }"
            :value="message.link.url"
          />
        </div>
        <div v-if="message.showMailBoxesButton" class="tw:flex tw:gap-2">
          <EdroneButton size="small" type="outline" @click="handleOpenMailBox('gmail')">
            <template #left-icon>
              <img alt="Mailbox icon" src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/Gmail.svg" />
            </template>
            {{ translations.gmail }}
          </EdroneButton>
          <EdroneButton size="small" type="outline" @click="handleOpenMailBox('outlook')">
            <template #left-icon>
              <img alt="Mailbox icon" src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/OutlookLogo.svg" />
            </template>
            {{ translations.outlook }}
          </EdroneButton>
        </div>
      </div>
    </div>

    <div
      class="tw:flex tw:items-center tw:justify-center tw:cursor-pointer tw:p-1.5 tw:w-6 tw:h-6 tw:rounded-sm tw:hover:bg-default-100"
      @click="handleCloseClick"
    >
      <img
        alt="Close icon"
        src="https://d3vhsxl1pwzf0p.cloudfront.net/EdroneToast/xmark.svg"
        style="width: 14px; height: 14px"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { ButtonType } from "edrone-components/EdroneButton.types";
import qrcode from "@chenfengyuan/vue-qrcode";

type ToastType = "default" | "info" | "warning" | "error" | "success";

export default defineComponent({
  name: "EdroneToast",
  components: {
    EdroneButton,
    qrcode,
  },
  props: {
    type: {
      type: String as PropType<ToastType>,
      required: true,
    },
    message: {
      type: [String, Object] as PropType<
        | string
        | {
            message: string;
            desc?: string;
            link?: {
              text: string;
              url?: string;
              newTab?: boolean;
              onClick?: () => void;
              buttonType?: ButtonType;
            } | null;
            showMailBoxesButton?: boolean;
          }
      >,
      default() {
        return {
          message: "Default message",
          desc: "",
          link: null,
        };
      },
    },
  },
  emits: ["link", "close-toast"],
  setup(props, context) {
    const translations = {
      ...window._trans._readyFlows.toast.toastSendTestEmailSuccess,
      preview: window._trans._crossSelling.preview,
    };
    function handleLinkClick(url: string, newTab: boolean = false) {
      if (newTab) {
        window.open(url, "_blank");
        return;
      }
      window.open(url, "_self");
    }

    function handleButtonClick(link: {
      text: string;
      url?: string;
      newTab?: boolean;
      onClick?: () => void;
      buttonType?: ButtonType;
    }) {
      if (link.onClick) {
        link.onClick();
      } else if (link.url) {
        handleLinkClick(link.url, link.newTab);
      }
    }

    function handleCloseClick() {
      context.emit("close-toast");
    }

    const handleOpenMailBox = (mailbox: string) => {
      const mailBoxes = {
        gmail: "https://mail.google.com/mail/#search/from:engagement-test@edrone.me OR from:no-reply@edrone.me",
        outlook: "https://outlook.live.com/mail/",
      };
      window.open(mailBoxes[mailbox], "_blank");
    };

    return {
      handleLinkClick,
      handleButtonClick,
      handleCloseClick,
      handleOpenMailBox,
      translations,
    };
  },
});
</script>

<style lang="scss">
.Vue-Toastification__container {
  z-index: 49999;
  font-family: "Roboto", sans-serif;
}

// CUSTOM ANIMATION
@keyframes edroneToastFadeIn {
  from {
    transform: translateY(-100px);
  }
  to {
    transform: translateY(0);
  }
}
@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}
.edrone-toast-fade-enter-active {
  animation-name: edroneToastFadeIn;
  animation-duration: 750ms;
  animation-fill-mode: both;
}
.edrone-toast-fade-leave-active {
  animation-name: fadeOut;
  animation-duration: 750ms;
  animation-fill-mode: both;
}
.edrone-toast-fade-move {
  transition-timing-function: ease-in-out;
  transition-property: all;
  transition-duration: 400ms;
}

.Vue-Toastification__container.top-right.edrone-toast-container {
  position: fixed;
  transition: right 0.25s ease-in 0s;
  justify-content: start;
  top: 40px;
  right: 40px;
}

.Vue-Toastification__toast.edrone-toast {
  min-height: unset;
  padding: 1rem;
  box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.22);
  border-radius: 8px;
  background: white;
}
</style>
